/**
 * Vera Home: the redesigned landing page (option 5b "Sunrise").
 *
 * Vera's home at `/` since 9/30: open the modules you can use, in a two-column
 * layout: greeting band and module zones on the left, Veraciter Central on the
 * right (stacked under the modules below 1024px). The layout is the handoff's;
 * the styling follows the Academy refresh rather than the handoff's own tokens.
 * Every deviation and its reason is in docs/design/home/PLAN.md. The ones that
 * shape this file:
 *
 *   - DM Sans comes from a nested FluentProvider, exactly as Academy scopes it,
 *     so this page moves with the font decision instead of ahead of it.
 *   - Each tile wears its own locked module accent (launcher decision
 *     2026-08-08), not the zone color: module color is how a user knows which
 *     app they are in.
 *   - Every zone sits on the same neutral gray (Sasha 9/27): the category
 *     color lives on the label dot, and the tiles carry the color.
 *   - Compact on purpose: the goal is all four zones on one laptop screen
 *     without scrolling (Sasha 9/27), so tiles flow into as many columns as
 *     fit rather than a fixed four, and nothing here is taller than it needs.
 *   - Tiles are cards you click INTO, the one place the refresh allows a lift,
 *     and it is a border plus a 3px rise, not a shadow.
 *
 * Zones and their order come from the admin-managed launcher categories
 * (Catalog > Configuration).
 *
 * The Spotlight (Sasha 10/02) never touches the Central column. While one is
 * live it takes a centre column on a wide screen (Sasha's ask), and a band
 * above the module zones below that width, where a third column would leave
 * the modules one tile wide. With no live spotlight the layout is unchanged.
 * Overlapping spotlights stack in that slot, newest start first (Sasha 10/12;
 * the server caps how many). `/?previewSpotlight=<id>` puts any spotlight (a
 * draft, a scheduled one) at the top of the stack for a Spotlight Admin, with
 * voting off.
 */

import { useEffect, useId, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FluentProvider, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { ShieldKeyholeRegular } from '@fluentui/react-icons';
import {
  DaybreakMark,
  DisplayHeading,
  Eyebrow,
  borderRadius,
  isLightColor,
  semanticColors,
  typography,
  useAccessibleModules,
  veracityColors,
  veracityLightTheme,
  type AccessibleModule,
} from '@shared/ui';
import { useAuth } from '@shared/providers/AuthProvider';
import { hasGlobalAdmin, canManageDemoPages } from '@shared/auth';
import { firstNameOf } from '@shared/lib';
import { useLauncherCategories, type LauncherCategoryConfig } from '@entities/admin-config';
import { useLauncherStats, statLineFor } from './useLauncherStats';
import { CentralPanel } from './CentralPanel';
import { SpotlightCard } from './central/SpotlightCard';
import { openMascot, useCentralSummary, useSpotlightPreview } from './central/api';
import { ViewAsUserBanner } from './central/ViewAsUser';

/** Same scoping as Academy's routeTree: see the note there before changing it. */
const homeTheme = { ...veracityLightTheme, fontFamilyBase: typography.fontFamily };

const HEX = /^#[0-9a-fA-F]{6}$/;

/** At or above this width a live Spotlight gets its own centre column. */
const SPOTLIGHT_COLUMN_QUERY = '(min-width: 1500px)';

function useWideScreen(): boolean {
  const [wide, setWide] = useState(() => window.matchMedia?.(SPOTLIGHT_COLUMN_QUERY).matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia?.(SPOTLIGHT_COLUMN_QUERY);
    if (!mq) return;
    const onChange = () => setWide(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return wide;
}

function greetingForNow(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

const useStyles = makeStyles({
  provider: {
    backgroundColor: 'transparent',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  page: {
    flex: 1,
    display: 'grid',
    // Two thirds modules, one third Central (Sasha 9/27), with a floor so
    // Central's rows never squeeze on a narrow laptop.
    gridTemplateColumns: 'minmax(0, 2fr) minmax(340px, 1fr)',
    gridTemplateRows: 'auto 1fr',
    alignItems: 'stretch',
    backgroundColor: '#FFFFFF',
    '@media (max-width: 1024px)': {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  // Three columns, only rendered at SPOTLIGHT_COLUMN_QUERY widths.
  pageSpotlight: {
    gridTemplateColumns: 'minmax(0, 1.7fr) minmax(320px, 1fr) minmax(340px, 1fr)',
  },
  spotlightColumn: {
    minWidth: 0,
    padding: '16px 4px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  spotlightBand: {
    padding: '16px 28px 0',
    maxWidth: '760px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },

  // --- The sky ----------------------------------------------------------------
  // ONE band across both columns, so the greeting and the Central header are
  // the same sky rather than two blocks meeting at a hard edge (Sasha 9/27).
  // It runs the palette's own day, which is what the brand names describe:
  // Veracity Orange at daybreak, through Daybreak Amber and Morning Coral, to
  // Twilight Plum behind Central. It starts on the brand ramp's hover shade
  // (#C23D19) because white 12px text needs the extra contrast there.
  sky: {
    gridColumn: '1 / -1',
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 2fr) minmax(340px, 1fr)',
    position: 'relative',
    overflow: 'hidden',
    backgroundImage: `linear-gradient(100deg, ${tokens.colorBrandBackgroundHover} 0%, ${veracityColors.veracityOrange} 26%, ${veracityColors.daybreakAmber} 50%, ${veracityColors.morningCoral} 60%, ${veracityColors.twilightPlum} 74%, ${veracityColors.twilightPlum} 100%)`,
    '@media (max-width: 1024px)': {
      gridTemplateColumns: 'minmax(0, 1fr)',
      backgroundImage: `linear-gradient(170deg, ${tokens.colorBrandBackgroundHover} 0%, ${veracityColors.veracityOrange} 35%, ${veracityColors.morningCoral} 60%, ${veracityColors.twilightPlum} 85%)`,
    },
  },
  // The greeting spans the modules and the spotlight; Central's header keeps
  // its own column.
  skySpotlight: {
    gridTemplateColumns: 'minmax(0, 1.7fr) minmax(320px, 1fr) minmax(340px, 1fr)',
  },
  bandSpotlight: {
    gridColumn: '1 / 3',
  },
  band: {
    position: 'relative',
    zIndex: 1,
    padding: '20px 28px 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  centralHead: {
    position: 'relative',
    padding: '20px 24px 18px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '2px',
  },
  // The OneVeracity daybreak mark, rising on the horizon at the far right of
  // the Central header (Sasha 9/27). Decorative only.
  sun: {
    position: 'absolute',
    right: '20px',
    bottom: '-34px',
    opacity: 0.3,
    pointerEvents: 'none',
    animationName: {
      from: { transform: 'translateY(36px)', opacity: 0 },
      to: { transform: 'translateY(0)', opacity: 0.3 },
    },
    animationDuration: '1.1s',
    animationTimingFunction: 'cubic-bezier(.2,.8,.3,1)',
    '@media (prefers-reduced-motion: reduce)': {
      animationName: 'none',
    },
    '@media (max-width: 1024px)': {
      display: 'none',
    },
  },
  // The mascot (Sasha 10/16): the Victory Bark's balloon dog, already cached,
  // standing in front of the sun on the days he is out. A click opens his
  // playground in its own window; nothing 3D loads until then.
  mascot: {
    position: 'absolute',
    right: '26px',
    bottom: '8px',
    zIndex: 1,
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    lineHeight: 0,
    transformOrigin: '50% 100%',
    animationName: {
      '0%, 100%': { transform: 'translateY(0) rotate(0deg)' },
      '50%': { transform: 'translateY(-4px) rotate(-3deg)' },
    },
    animationDuration: '2.4s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'ease-in-out',
    ':hover': { animationDuration: '0.5s' },
    ':focus-visible': { outline: `2px solid ${veracityColors.daybreakAmber}`, outlineOffset: '4px', borderRadius: '8px' },
    '@media (prefers-reduced-motion: reduce)': { animationName: 'none' },
    '& img': { height: '58px', width: 'auto', filter: 'drop-shadow(0 3px 4px rgba(0,0,0,.25))' },
  },
  // A Spotlight Admin sees him on his days off, faded, to test.
  mascotOff: {
    opacity: 0.45,
    animationName: 'none',
  },
  // "Veraciter" in handwriting (Sasha 9/27), sitting on "Central" like a
  // signature: Caveat, larger than the word below it, a slight tilt.
  centralKicker: {
    position: 'relative',
    alignSelf: 'flex-start',
    fontFamily: "'Caveat', 'Segoe Script', cursive",
    fontSize: '34px',
    fontWeight: 700,
    lineHeight: 0.9,
    color: veracityColors.daybreakAmber,
    transform: 'rotate(-4deg)',
    transformOrigin: 'left bottom',
    marginBottom: '2px',
  },
  centralTitle: {
    position: 'relative',
    margin: 0,
    fontSize: '32px',
    fontWeight: 700,
    letterSpacing: '-0.03em',
    lineHeight: 1.02,
    color: '#FFFFFF',
  },
  bandEyebrow: {
    color: '#FFFFFF',
  },
  bandHeading: {
    color: '#FFFFFF',
  },

  // --- Zones ------------------------------------------------------------------
  // Sections in columns (Sasha 9/27): two on a laptop, three on a wide
  // screen, one on a phone. Row-major, so the configured order reads left to
  // right, and each section keeps its own height rather than stretching to
  // its neighbour's.
  zones: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    alignItems: 'start',
    gap: '10px',
    padding: '16px 28px 24px',
  },
  zone: {
    borderRadius: borderRadius.editorial,
    padding: '10px 12px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    backgroundColor: tokens.colorNeutralBackground3,
  },
  zoneLabel: {
    alignSelf: 'flex-start',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 10px',
    borderRadius: borderRadius.circular,
    backgroundColor: '#FFFFFF',
    color: veracityColors.midnightCharcoal,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
  },
  zoneDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  // Tiles stack as a list inside their section.
  grid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },

  // --- Tiles ------------------------------------------------------------------
  // Always visible (Sasha 9/27): icon, then the name, on a white tile a shade
  // off the zone gray. Hover or keyboard focus lifts it (the one place the
  // refresh allows a lift) and fades the description, plus the live signal
  // where a module has one, into the empty space right of the name. No
  // floating tooltip: nothing overlaps the tile below.
  tile: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    minHeight: '48px',
    padding: '6px 12px 6px 8px',
    boxSizing: 'border-box',
    borderRadius: borderRadius.large,
    backgroundColor: 'rgba(255, 255, 255, 0.72)',
    border: '1px solid rgba(28, 25, 25, 0.06)',
    textDecoration: 'none',
    color: veracityColors.midnightCharcoal,
    outlineOffset: '2px',
    transitionProperty: 'transform, background-color, border-color',
    transitionDuration: '160ms',
    transitionTimingFunction: 'ease',
    ':hover': {
      backgroundColor: '#FFFFFF',
      border: `1px solid ${semanticColors.borderDefault}`,
      transform: 'translateY(-1px)',
    },
    ':focus-visible': { backgroundColor: '#FFFFFF' },
    ':hover .home-detail': { opacity: 1 },
    ':focus-visible .home-detail': { opacity: 1 },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0ms',
      ':hover': { transform: 'none' },
    },
  },
  disc: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    fontSize: '18px',
    flexShrink: 0,
  },
  tileName: {
    flexShrink: 0,
    maxWidth: '45%',
    fontSize: '14px',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // In the DOM all the time (the link's aria-describedby points here, so a
  // screen reader hears it without hovering); only its opacity changes.
  detail: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    paddingLeft: '10px',
    borderLeft: `1px solid ${semanticColors.borderSubtle}`,
    opacity: 0,
    transitionProperty: 'opacity',
    transitionDuration: '160ms',
  },
  detailText: {
    fontSize: '12px',
    lineHeight: 1.35,
    color: semanticColors.textSecondary,
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
  },
  detailTextOneLine: {
    WebkitLineClamp: 1,
  },
  detailSignal: {
    fontSize: '11.5px',
    fontWeight: 600,
    color: tokens.colorBrandForegroundLink,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  empty: {
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    color: veracityColors.duskBlue,
  },
});

interface TileProps {
  to: string;
  name: string;
  description: string;
  signal?: string | null;
  accent: string;
  icon: AccessibleModule['module']['icon'];
}

function ModuleTile({ to, name, description, signal, accent, icon: Icon }: TileProps) {
  const styles = useStyles();
  const detailId = useId();
  const glyph = isLightColor(accent) ? veracityColors.midnightCharcoal : '#FFFFFF';
  return (
    <Link to={to} className={styles.tile} aria-describedby={detailId}>
      <span className={styles.disc} style={{ backgroundColor: accent, color: glyph }} aria-hidden>
        <Icon />
      </span>
      <span className={styles.tileName}>{name}</span>
      <span id={detailId} className={mergeClasses(styles.detail, 'home-detail')}>
        <span className={mergeClasses(styles.detailText, Boolean(signal) && styles.detailTextOneLine)}>
          {description}
        </span>
        {signal && <span className={styles.detailSignal}>{signal}</span>}
      </span>
    </Link>
  );
}

export function HomePage() {
  const styles = useStyles();
  const { user, roles } = useAuth();
  const firstName = firstNameOf(user?.name);
  const isGlobalAdmin = hasGlobalAdmin(roles);
  const showAdminTile = isGlobalAdmin || canManageDemoPages(roles);

  const launcherCfg = useLauncherCategories();
  const statsQuery = useLauncherStats();
  const modules = useAccessibleModules();
  // The same query CentralPanel reads; React Query shares the one request.
  const central = useCentralSummary();
  const [params] = useSearchParams();
  const isSpotlightAdmin = central.data?.isSpotlightAdmin ?? false;
  const mascot = central.data?.enabled ? central.data.mascot : null;
  const previewId = isSpotlightAdmin ? params.get('previewSpotlight') : null;
  const preview = useSpotlightPreview(previewId);
  const live = central.data?.enabled ? central.data.spotlights : [];
  const previewed = preview.data;
  const spotlights = previewed ? [previewed, ...live.filter((s) => s.id !== previewed.id)] : live;
  const wide = useWideScreen();
  const spotlightColumn = spotlights.length > 0 && wide;
  const spotlightCards = spotlights.map((s) => (
    <SpotlightCard
      key={s.id}
      spotlight={s}
      canManage={isSpotlightAdmin}
      preview={s.id === previewed?.id}
    />
  ));

  // Ideas lives in Veraciter Central, not the grid. "Soon" placeholders are
  // dropped: a tile that cannot be opened is not something to launch.
  const hasIdeas = modules.some(({ module, hasAccess }) => module.id === 'ideas' && hasAccess);
  const gridModules = modules.filter(
    ({ module, hasAccess }) => hasAccess && module.status === 'live' && !module.tool && module.id !== 'ideas'
  );

  // Sections come from the admin launcher config, with a
  // catch-all for modules assigned to a category an admin has since removed.
  const categories: LauncherCategoryConfig[] = [...launcherCfg.categories];
  const categoryIds = new Set(categories.map((c) => c.id));
  const fallbackCategory: LauncherCategoryConfig = {
    id: '__other',
    label: 'More',
    color: veracityColors.duskBlue,
  };
  const categoryOf = (moduleId: string, builtIn: string): string => {
    const assigned = launcherCfg.assignments[moduleId] ?? builtIn;
    if (categoryIds.has(assigned)) return assigned;
    if (categoryIds.has(builtIn)) return builtIn;
    return fallbackCategory.id;
  };
  const byCategory = new Map<string, AccessibleModule[]>();
  for (const entry of gridModules) {
    const cid = categoryOf(entry.module.id, entry.module.category);
    const list = byCategory.get(cid);
    if (list) list.push(entry);
    else byCategory.set(cid, [entry]);
  }
  const adminCategoryId = categoryOf('admin', 'tools');
  if (byCategory.has(fallbackCategory.id) || adminCategoryId === fallbackCategory.id) {
    categories.push(fallbackCategory);
  }
  // Tools is always the last section (Sasha 9/27), whatever the saved order.
  const toolsAt = categories.findIndex((c) => c.id === 'tools');
  if (toolsAt >= 0) categories.push(...categories.splice(toolsAt, 1));

  const accentFor = (m: AccessibleModule['module']): string => {
    const override = launcherCfg.moduleColors?.[m.id];
    return override && HEX.test(override) ? override : m.accent;
  };

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  const zones = categories
    .map((category) => {
      const entries = byCategory.get(category.id) ?? [];
      const showAdminHere = category.id === adminCategoryId && showAdminTile;
      if (entries.length === 0 && !showAdminHere) return null;
      const color = HEX.test(category.color) ? category.color : veracityColors.duskBlue;
      return (
        <section
          key={category.id}
          className={styles.zone}
          aria-label={category.label}
        >
          <span className={styles.zoneLabel}>
            <span className={styles.zoneDot} style={{ backgroundColor: color }} aria-hidden />
            {category.label}
          </span>
          <div className={styles.grid}>
            {entries.map(({ module: m }) => (
              <ModuleTile
                key={m.id}
                to={m.path}
                name={m.name}
                description={m.description}
                signal={statLineFor(m.id, statsQuery.data)}
                accent={accentFor(m)}
                icon={m.icon}
              />
            ))}
            {showAdminHere && (
              <ModuleTile
                to="/admin"
                name="Administration"
                description={
                  isGlobalAdmin
                    ? 'Role assignments, user activity, audit log, and public demo pages.'
                    : 'Manage public demo pages.'
                }
                accent={veracityColors.midnightCharcoal}
                icon={ShieldKeyholeRegular}
              />
            )}
          </div>
        </section>
      );
    })
    .filter(Boolean);

  return (
    <FluentProvider theme={homeTheme} className={styles.provider}>
      <div className={mergeClasses(styles.page, spotlightColumn && styles.pageSpotlight)}>
        <div className={mergeClasses(styles.sky, spotlightColumn && styles.skySpotlight)}>
          <header className={mergeClasses(styles.band, spotlightColumn && styles.bandSpotlight)}>
            <Eyebrow className={styles.bandEyebrow}>{today}</Eyebrow>
            <DisplayHeading size={100} className={styles.bandHeading}>
              {greetingForNow()}
              {firstName ? `, ${firstName}` : ''}.
            </DisplayHeading>
          </header>
          <div className={styles.centralHead}>
            <DaybreakMark height={96} color={veracityColors.daybreakAmber} className={styles.sun} />
            <span className={styles.centralKicker}>Veraciter</span>
            <h2 id="central-title" className={styles.centralTitle}>
              Central
            </h2>
            {mascot && (mascot.out || isSpotlightAdmin) && (
              <button
                type="button"
                className={mergeClasses(styles.mascot, !mascot.out && styles.mascotOff)}
                onClick={openMascot}
                title={
                  mascot.out
                    ? `Play with ${mascot.name}`
                    : `${mascot.name} is hidden from everyone else today. Spotlight Admins see him to test.`
                }
                aria-label={`Play with ${mascot.name} (opens a new window)`}
              >
                <img src="/balloon-dog.webp" alt="" />
              </button>
            )}
          </div>
        </div>
        <div className={styles.main}>
          <ViewAsUserBanner />
          {spotlights.length > 0 && !spotlightColumn && (
            <div className={styles.spotlightBand}>{spotlightCards}</div>
          )}
          <div className={styles.zones}>
            {zones.length > 0 ? (
              zones
            ) : (
              <div className={styles.empty}>
                You don't currently have access to any Vera modules. Ask a Vera admin to
                assign you the appropriate role.
              </div>
            )}
          </div>
        </div>
        {spotlightColumn && <div className={styles.spotlightColumn}>{spotlightCards}</div>}
        <CentralPanel
          showIdeas={hasIdeas}
          ideas={statsQuery.data?.ideas}
        />
      </div>
    </FluentProvider>
  );
}
